import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Card } from "~/components/ui/card";
import { Plus, MessageCircle, ArrowRight, User } from "lucide-react";
import { ButtonLink } from "~/components/ui/button";
import { useUser } from "@clerk/tanstack-react-start";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { useQuery } from "@tanstack/react-query";
import { getUserConversations } from "~/lib/functions/messaging";
import { formatDistanceToNow } from "date-fns";

function DashboardPage() {
  const { user, isLoaded } = useUser();

  const { data: conversationsData } = useQuery({
    queryKey: ["user-conversations"],
    queryFn: () => getUserConversations({ data: { limit: 3, offset: 0 } }),
    enabled: isLoaded,
  });

  const conversations = conversationsData?.conversations || [];
  const totalConversations = conversations.length;
  const unreadCount = conversations.reduce(
    (sum, conv) => sum + conv.unreadCount,
    0,
  );

  if (!isLoaded) {
    return (
      <div className="mb-10.5 mx-auto my-4 flex w-full max-w-screen-xl flex-col overflow-visible px-4 md:mt-8">
        <div className="animate-pulse">
          <div className="h-9 bg-gray-200 rounded mb-6 w-80"></div>
          <div className="h-6 bg-gray-200 rounded w-60"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-10.5 mx-auto my-4 flex w-full max-w-screen-xl flex-col overflow-visible px-4 md:mt-8">
        <h2 className="font-heading mb-6 text-3xl font-bold">
          Welcome, {user?.firstName || "User"} {user?.lastName}!
        </h2>
        <h4 className="text-text-secondary">Ready to continue your journey?</h4>

        <div className="mt-10.5 grid gap-6 md:grid-cols-2">
          <Card className="flex w-full flex-col space-y-4 p-8">
            <div className="flex items-center space-x-3">
              <div className="rounded-full bg-blue-100 p-3">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">My Conversations</h2>
                {unreadCount > 0 && (
                  <div className="mt-1 inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                    {unreadCount} unread message{unreadCount > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
            <p className="text-gray-500">
              {totalConversations > 0
                ? `You have ${totalConversations} conversation${totalConversations > 1 ? "s" : ""} with coaches.`
                : "Start conversations with coaches to get personalized guidance."}
            </p>

            {conversations.length > 0 ? (
              <div className="space-y-3">
                {conversations.slice(0, 2).map((conversation) => {
                  const coachName =
                    `${conversation.otherParticipant.firstName || ""} ${conversation.otherParticipant.lastName || ""}`.trim() ||
                    "Coach";
                  return (
                    <Link
                      key={conversation.id}
                      to="/messages/$conversationId"
                      params={{ conversationId: conversation.id }}
                      className="block rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="rounded-full bg-gray-100 p-1.5">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {coachName}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {conversation.subject}
                          </p>
                          <p className="text-xs text-gray-400">
                            {conversation.lastMessageAt
                              ? formatDistanceToNow(
                                  new Date(conversation.lastMessageAt),
                                  { addSuffix: true },
                                )
                              : ""}
                          </p>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <div className="rounded-full bg-blue-600 px-2 py-1 text-xs font-medium text-white">
                            {conversation.unreadCount}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
                {totalConversations > 2 && (
                  <Link
                    to="/messages"
                    className="flex items-center justify-center space-x-2 rounded-lg border border-dashed border-gray-300 p-3 text-gray-600 transition-colors hover:bg-gray-50"
                  >
                    <span className="text-sm">
                      View all {totalConversations} conversations
                    </span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ) : (
              <Link
                to="/messages"
                className="flex items-center justify-center space-x-2 rounded-lg border border-dashed border-gray-300 p-4 text-gray-600 transition-colors hover:bg-gray-50"
              >
                <MessageCircle className="h-5 w-5" />
                <span>View Messages</span>
              </Link>
            )}
          </Card>

          <Card className="flex w-full flex-col space-y-4 p-8">
            <div className="flex items-center space-x-3">
              <div className="rounded-full bg-green-100 p-3">
                <Plus className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold">Coach Discovery</h2>
            </div>
            <p className="text-gray-500">
              Explore our network of experienced coaches tailored to your needs.
            </p>

            <ButtonLink
              href="/coach-discovery/"
              type="button"
              variant="fancy"
              className="py-4.5 h-15 mb-2 w-fit px-8 text-xl"
            >
              <Plus className="mr-2 h-6 w-6" />
              Discover Coaches
            </ButtonLink>
          </Card>
        </div>

        <Card className="mt-6 flex w-full flex-col space-y-4 p-8">
          <div className="flex items-center space-x-3">
            <div className="rounded-full bg-purple-100 p-3">
              <User className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold">Account Settings</h2>
          </div>
          <p className="text-gray-500">
            Customize your profile, update your details, and manage your
            coaching preferences.
          </p>

          <Link
            to="/settings/profile"
            className="text-text-primary/100 font-600 w-fit hover:underline"
          >
            Edit Profile & Settings
          </Link>
        </Card>
      </div>
    </>
  );
}

export const Route = createFileRoute("/_user/dashboard")({
  head: () =>
    createNoIndexMeta(
      "Dashboard | Dream Coach Match",
      "Your coaching journey dashboard with conversations and coach discovery",
    ),
  component: DashboardPage,
});
